import { Fragment, useRef, useState } from "react";
import { Dialog, Transition, Disclosure } from "@headlessui/react";
import {
  ExclamationTriangleIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import Loader from "../../components/Loader";

interface Record {
  id: number;
  name: string;
}

export interface AffectedRecords {
  locations: Record[];
  orderAheadLocations: Record[];
  eventsPayments: Record[];
  eventsTicketing: Record[];
  eventsInvoicing: Record[];
}

interface DeactivateProcessorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  affectedRecords: AffectedRecords;
  processorName: string;
  mode: "deactivate" | "delete";
}

const categories: { key: keyof AffectedRecords; label: string }[] = [
  { key: "locations", label: "Locations" },
  { key: "orderAheadLocations", label: "Order Ahead Locations" },
  { key: "eventsPayments", label: "Events (Payments)" },
  { key: "eventsTicketing", label: "Events (Ticketing)" },
  { key: "eventsInvoicing", label: "Events (Invoicing)" },
];

function exportToCSV(affectedRecords: AffectedRecords, processorName: string) {
  const rows: string[][] = [["Category", "Record ID", "Record Name"]];
  const categoryLabels: { key: keyof AffectedRecords; label: string }[] = categories;

  for (const { key, label } of categoryLabels) {
    for (const record of affectedRecords[key]) {
      rows.push([label, String(record.id), record.name]);
    }
  }

  const csvContent = rows
    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${processorName}_affected_records.csv`;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function DeactivateProcessorModal({
  isOpen,
  onClose,
  onConfirm,
  affectedRecords,
  processorName,
  mode,
}: DeactivateProcessorModalProps) {
  const cancelButtonRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const totalRecords = categories.reduce(
    (sum, { key }) => sum + affectedRecords[key].length,
    0
  );

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  };

  const title =
    mode === "delete"
      ? `Deleting ${processorName}`
      : `Deactivating ${processorName}`;

  const confirmLabel =
    mode === "delete" ? "Confirm & Delete" : "Confirm & Deactivate";

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                    <div className="flex items-center justify-between">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        {title}
                      </Dialog.Title>
                      {totalRecords > 0 && (
                        <button
                          type="button"
                          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                          onClick={() =>
                            exportToCSV(affectedRecords, processorName)
                          }
                        >
                          Export CSV
                        </button>
                      )}
                    </div>
                    <div className="mt-2">
                      {totalRecords > 0 ? (
                        <p className="text-sm text-gray-500">
                          The following records will have this processor removed
                          and set to PENDING:
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500">
                          No records are currently using this processor.
                        </p>
                      )}
                    </div>

                    {totalRecords > 0 && (
                      <div className="mt-4 max-h-80 overflow-y-auto space-y-2">
                        {categories.map(({ key, label }) => {
                          const records = affectedRecords[key];
                          if (records.length === 0) return null;
                          return (
                            <Disclosure key={key} defaultOpen>
                              {({ open }) => (
                                <div className="border border-gray-200 rounded-md">
                                  <Disclosure.Button className="flex w-full justify-between items-center px-4 py-2 text-left text-sm font-medium text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-t-md">
                                    <span>
                                      {label}{" "}
                                      <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-800">
                                        {records.length}
                                      </span>
                                    </span>
                                    <ChevronUpIcon
                                      className={`${
                                        open ? "rotate-180 transform" : ""
                                      } h-4 w-4 text-gray-500 transition-transform`}
                                    />
                                  </Disclosure.Button>
                                  <Disclosure.Panel className="px-4 py-2">
                                    <table className="min-w-full text-sm">
                                      <thead>
                                        <tr className="border-b border-gray-200">
                                          <th className="text-left py-1 pr-4 font-medium text-gray-500">
                                            ID
                                          </th>
                                          <th className="text-left py-1 font-medium text-gray-500">
                                            Name
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {records.map((record) => (
                                          <tr
                                            key={record.id}
                                            className="border-b border-gray-100 last:border-0"
                                          >
                                            <td className="py-1 pr-4 text-gray-600">
                                              {record.id}
                                            </td>
                                            <td className="py-1 text-gray-900">
                                              {record.name}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </Disclosure.Panel>
                                </div>
                              )}
                            </Disclosure>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={handleConfirm}
                    disabled={isLoading}
                  >
                    {isLoading ? <Loader /> : confirmLabel}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={onClose}
                    ref={cancelButtonRef}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
